import React, {ChangeEvent, FormEvent, useEffect, useRef, useState} from 'react';
import {Button, Checkbox, Container, FormControlLabel, Grid, TextField, Typography} from '@mui/material';
import {red} from '@mui/material/colors';
import useGlobalStyles from "../styles/global";
import useAgeGateStyles from '../styles/age_gate';
import goat_gif from '../assets/images/goat-this.gif';
import ModalWrapper from "./ModalWrapper";

interface AgeCheckerProps {
    onAgeSubmit: (age: number, keepIt: boolean) => void;
}

const AgeGate: React.FC<AgeCheckerProps> = ({onAgeSubmit}) => {
    const [year, setYear] = useState<string[]>(['', '', '', '']);
    const [month, setMonth] = useState<number | null>(null)
    const [day, setDay] = useState<number | null>(null)
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const [error, setError] = useState<string>('');
    const [secondStep, setSecondStep] = useState<boolean>(false);
    const [keepIt, setKeepIt] = useState<boolean>(false);
    const [allowEntry, setAllowEntry] = useState<boolean>(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const globalStyle = useGlobalStyles();
    const ageGateStyle = useAgeGateStyles();

    const redirectUrl = 'https://www.alcoolulnutefacemare.ro';

    useEffect(() => {
        resetState();
    }, []);

    const resetState = () => {
        setSecondStep(false);
        setYear(['', '', '', '']);
        setMonth(null);
        setDay(null);
    }
    const resetError = () => {
        setError('');
    }
    const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        resetError();
        const value = event.target.value;
        if (/^\d?$/.test(value)) {
            const newYear = [...year];
            newYear[index] = value;
            setYear(newYear);

            if (value && index < 3) {
                inputsRef.current[index + 1]?.focus();
            }
            
            setAllowEntry(newYear.every(digit => digit.length === 1));
        }
    };

    const handleBackspace = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !year[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
        resetError();
    };

    const setErrorUnder18 = () => {
        setError('Acest site este interzis persoanelor sub 18 ani.');
        setAllowEntry(false);
        setModalIsOpen(true);
        setTimeout(() => {
            window.location.href = redirectUrl;
        }, 3000);
    }

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (secondStep) {
            calcByBirthday();
            return;
        }
        calcByYear();
    };

    const calcByBirthday = () => {
        if (month === null || day === null || month === 0 || month > 12 || day === 0) {
            setError('Data nasterii este invalida');
            return;
        }
        const birthDate: Date = new Date(parseInt(year.join('')), month - 1, day);
        const currentDate: Date = new Date();
        const age = getAge(birthDate, currentDate);
        resetState();
        if ( age < 18) {
            setErrorUnder18();
            return;
        }

        onAgeSubmit(age, keepIt);
    }

    const calcByYear = () => {
        const enteredYear = parseInt(year.join(''));
        const currentYear = (new Date()).getFullYear();
        const age = currentYear - enteredYear;
        if (age === 18) {
            setSecondStep(true);
            return;
        }
        if (age < 18) {
            setErrorUnder18();
            return;
        }

        onAgeSubmit(age, keepIt);
    }

    const getAge = (dob: Date, when: Date) => {
        let age = 0;

        if (dob.getMonth() > when.getMonth()) {
            age = -1;
        }
        if (dob.getMonth() === when.getMonth() && dob.getDate() > when.getDate()) {
            age = -1;
        }

        return age + when.getFullYear() - dob.getFullYear() ;

    }
    
    const handleChangeMonth = (e: ChangeEvent<HTMLInputElement>) => {
        resetError();
        const value = e.target.value;
        if (/^\d{0,2}$/.test(value)) {
            let parsedValue = parseInt(value);
            if (parsedValue < 1) {
                parsedValue = 1;
            }
            if (parsedValue > 12) {
                parsedValue = 12;
            }
            setMonth(parsedValue);
        }
    }
    const handleChangeDay = (e: ChangeEvent<HTMLInputElement>) => {
        resetError();
        const value = e.target.value;
        if (/^\d{0,2}$/.test(value)) {
            let parsedValue = parseInt(value);
            if (parsedValue < 1) {
                parsedValue = 1;
            }
            switch (month) {
                case 1:
                case 3:
                case 5:
                case 7:
                case 8:
                case 10:
                case 12:
                    if (parsedValue > 31) {
                        parsedValue = 31;
                    }
                    break;
                case 2:
                    if (parsedValue > 29) {
                        parsedValue = 29;
                    }
                    break;
                case 4:
                case 6:
                case 9:
                case 11:
                    if (parsedValue > 30) {
                        parsedValue = 30;
                    }
                    break;
            }
            setDay(parsedValue);
        }
    }

    return (
        <Container maxWidth="md" className={globalStyle.container}>
            <Container className={ageGateStyle.container}>
                <Container className={ageGateStyle.formWrapper}>
                    <img src={goat_gif} alt={'Cool Goat'} className={ageGateStyle.goatGif}/>
                    <Typography className={ageGateStyle.formTitle}>Welcome! We are glad you're here :)</Typography>
                    <Typography className={ageGateStyle.formSubTitle}>Please input your date of birth</Typography>
                    {error.length > 0 && <Typography style={{color: red[300]}} variant="h4" gutterBottom>{error}</Typography>}
                    <form onSubmit={handleSubmit}>
                        {secondStep ?
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        className={ageGateStyle.formInput}
                                        label={'Month'}
                                        value={month || ''}
                                        type={'number'}
                                        
                                        onChange={handleChangeMonth}
                                        variant="outlined"
                                        inputProps={{style: {textAlign: 'center'}}}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        className={ageGateStyle.formInput}
                                        label={'Day'}
                                        type={'number'}
                                        value={day || ''}
                                        onChange={handleChangeDay}
                                        variant="outlined"
                                        inputProps={{style: {textAlign: 'center'}}}
                                    />
                                </Grid>
                            </Grid> :
                            <Grid container spacing={2}>
                                {year.map((digit, index) => (
                                    <Grid item xs={3} key={index}>
                                        <TextField
                                            className={ageGateStyle.formInput}
                                            inputRef={(el) => (inputsRef.current[index] = el)}
                                            value={digit}
                                            onChange={handleChange(index)}
                                            onKeyDown={handleBackspace(index)}
                                            variant="outlined"
                                            inputProps={{maxLength: 1, style: {textAlign: 'center'}}}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        }

                        <Button type="submit" variant="contained" color="primary" disabled={!allowEntry} className={ageGateStyle.formSubmit}>
                            Enter
                        </Button>
                        <FormControlLabel className={ageGateStyle.checkboxKeepIt} control={<Checkbox checked={keepIt} onChange={e => setKeepIt(e.target.checked)} />} label="Keep me logged in" />
                    </form>
                </Container>
            </Container>
            <Container className={ageGateStyle.footer}>
                <Typography className={ageGateStyle.textCookie} >We use cookies for enhancing your experience.</Typography>
                <Typography className={ageGateStyle.textPolicy}>By accessing this website you agree with our Confidentiality Policy and ToS.</Typography>
            </Container>

            <ModalWrapper isOpen={modalIsOpen} customClass='fillScreenContainer' onRequestClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return; 
                }
                setModalIsOpen(false);
            }}>
                <Container className={ageGateStyle.container}>
                    <Container className={ageGateStyle.formWrapper}>
                        <img src={goat_gif} alt={'Cool Goat'} className={ageGateStyle.goatGif}/>
                        <Typography className={ageGateStyle.formTitle} style={{color: red[300]}} variant="h4" gutterBottom>{error}</Typography>                    
                        <Typography className={ageGateStyle.formSubTitle} variant="h4" mt={5} gutterBottom>Vei fi redirecționat in scurt timp.</Typography>    
                    </Container>
                </Container>
            </ModalWrapper>
        </Container>
    );
};

export default AgeGate;
